// Client-side image optimizer.
//
// Everything the admin uploads is re-encoded to WebP before it reaches Firebase
// Storage, so customers download ~100 KB instead of multi-megabyte camera photos.
//
// The previous implementation trusted `canvas.toBlob(cb, 'image/webp')` to return
// null when WebP was unsupported. It does not: per the HTML spec the browser
// silently falls back to image/png, which is usually *larger* than the source
// JPEG, so the "compression didn't help" guard shipped the original file instead.
// Every encode here is therefore verified against the blob's real `type`.

// iOS Safari refuses to rasterise canvases beyond ~16.7M pixels and returns null
// from toBlob, which previously also fell through to "upload the original".
const MAX_CANVAS_PIXELS = 16777216;

const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.5];
const SCALE_STEPS = [1, 0.75, 0.55];

// Animations and vectors would be destroyed by a canvas round-trip.
const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml', 'image/apng']);

let webpEncodeSupport = null;

export function supportsWebpEncoding() {
	if (!webpEncodeSupport) {
		webpEncodeSupport = (async () => {
			try {
				const probe = document.createElement('canvas');
				probe.width = 4;
				probe.height = 4;
				const blob = await new Promise(resolve => {
					probe.toBlob(b => resolve(b || null), 'image/webp', 0.8);
				});
				return !!blob && blob.type === 'image/webp';
			} catch {
				return false;
			}
		})();
	}
	return webpEncodeSupport;
}

export function formatBytes(bytes) {
	const n = Number(bytes) || 0;
	if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
	if (n >= 1024) return `${Math.round(n / 1024)} KB`;
	return `${n} B`;
}

function sanitizeBaseName(name) {
	return (name || 'image')
		.toString()
		.replace(/\.[^/.]+$/, '')
		.replace(/[^a-zA-Z0-9._-]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 100) || 'image';
}

async function decode(file) {
	if (typeof createImageBitmap === 'function') {
		try {
			return { source: await createImageBitmap(file), close: true };
		} catch {
			// Fall through to the <img> path below; some browsers reject exotic files.
		}
	}
	const url = URL.createObjectURL(file);
	try {
		const img = await new Promise((resolve, reject) => {
			const el = new Image();
			el.onload = () => resolve(el);
			el.onerror = reject;
			el.src = url;
		});
		return { source: img, close: false, width: img.naturalWidth, height: img.naturalHeight };
	} finally {
		URL.revokeObjectURL(url);
	}
}

// Encodes at the requested type and verifies what actually came back, because a
// browser without WebP support quietly substitutes PNG.
async function encode(canvas, mime, quality) {
	const blob = await new Promise(resolve => {
		try {
			canvas.toBlob(b => resolve(b || null), mime, quality);
		} catch {
			resolve(null);
		}
	});
	if (!blob || blob.size === 0) return null;
	if (blob.type !== mime) return null;
	return blob;
}

function drawTo(source, width, height) {
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext('2d', { alpha: true });
	if (!ctx) return null;
	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = 'high';
	ctx.drawImage(source, 0, 0, width, height);
	return canvas;
}

/**
 * Re-encodes an image to WebP (JPEG where WebP encoding is unavailable), shrinking
 * it until it fits within `targetBytes`.
 *
 * Always resolves to something usable: on any failure it returns the original file
 * rather than throwing, so a save can never be blocked by optimisation.
 *
 * @returns {Promise<{file: File, originalSize: number, optimizedSize: number,
 *   converted: boolean, outputType: string, width: number, height: number,
 *   skipped: string｜null}>}
 */
export async function optimizeImage(file, options = {}) {
	const {
		maxWidth = 1600,
		maxHeight = 1600,
		targetBytes = 260 * 1024
	} = options;

	const result = {
		file,
		originalSize: file?.size || 0,
		optimizedSize: file?.size || 0,
		converted: false,
		outputType: file?.type || '',
		width: 0,
		height: 0,
		quality: 0,
		skipped: null
	};

	if (!file || !file.type || !file.type.startsWith('image/')) {
		result.skipped = 'not-an-image';
		return result;
	}
	if (SKIP_TYPES.has(file.type)) {
		result.skipped = 'animated-or-vector';
		return result;
	}

	let decoded;
	try {
		decoded = await decode(file);
	} catch {
		result.skipped = 'decode-failed';
		return result;
	}

	const source = decoded.source;
	const srcWidth = decoded.width || source.width;
	const srcHeight = decoded.height || source.height;

	try {
		if (!srcWidth || !srcHeight) {
			result.skipped = 'no-dimensions';
			return result;
		}

		const useWebp = await supportsWebpEncoding();
		const mime = useWebp ? 'image/webp' : 'image/jpeg';

		const fitScale = Math.min(maxWidth / srcWidth, maxHeight / srcHeight, 1);
		let best = null;

		outer:
		for (const scaleStep of SCALE_STEPS) {
			let width = Math.max(1, Math.round(srcWidth * fitScale * scaleStep));
			let height = Math.max(1, Math.round(srcHeight * fitScale * scaleStep));

			// Respect the canvas pixel ceiling so toBlob cannot fail outright.
			const pixels = width * height;
			if (pixels > MAX_CANVAS_PIXELS) {
				const shrink = Math.sqrt(MAX_CANVAS_PIXELS / pixels);
				width = Math.max(1, Math.floor(width * shrink));
				height = Math.max(1, Math.floor(height * shrink));
			}

			const canvas = drawTo(source, width, height);
			if (!canvas) break;

			for (const quality of QUALITY_STEPS) {
				// eslint-disable-next-line no-await-in-loop
				let blob = await encode(canvas, mime, quality);
				// A browser that cannot encode WebP still encodes JPEG fine.
				if (!blob && mime === 'image/webp') {
					// eslint-disable-next-line no-await-in-loop
					blob = await encode(canvas, 'image/jpeg', quality);
				}
				if (!blob) continue;

				if (!best || blob.size < best.blob.size) best = { blob, width, height, quality };
				if (blob.size <= targetBytes) break outer;
			}
		}

		if (!best) {
			result.skipped = 'encode-failed';
			return result;
		}
		// Never make things worse: a small PNG logo can beat its own WebP encode.
		if (best.blob.size >= result.originalSize) {
			result.skipped = 'original-smaller';
			return result;
		}

		const ext = best.blob.type === 'image/webp' ? 'webp' : 'jpg';
		const optimized = new File(
			[best.blob],
			`${sanitizeBaseName(file.name)}.${ext}`,
			{ type: best.blob.type, lastModified: Date.now() }
		);

		result.file = optimized;
		result.optimizedSize = optimized.size;
		result.converted = true;
		result.outputType = best.blob.type;
		result.width = best.width;
		result.height = best.height;
		result.quality = best.quality;
		return result;
	} catch {
		result.skipped = 'error';
		return result;
	} finally {
		if (decoded.close && typeof source.close === 'function') source.close();
	}
}
